import { jsx as _jsx } from "react/jsx-runtime";
import { useLocation } from '@hellodarwin/core/lib/features/providers/location-provider';
import { useLogin } from '@hellodarwin/app-core/lib/providers/login-provider';
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { ASSISTANT_EMBEDDED_CONVERSATION_PARAM, AssistantStatus, } from '../constants/assistant.constants';
import { useApiUrl, useAssistantApi } from '../hooks/useAssistantApi';
import { clearConversationBlockingError, clearConversationProcessingNotice, markConversationProcessingNotice, setAssistantStatus, } from '../redux';
import { useAssistantDispatch, useAssistantSelector, } from '../redux/assistant-hooks';
import { addConversation, setActiveConversation, updateConversation, updateConversationActivity, } from '../redux/assistantConversationSlice';
import { addMessage } from '../redux/assistantMessageSlice';
import { selectActiveConversation, selectAssistantStatus, selectConversationStillProcessing, } from '../redux/selectors';
import { createConversation as createConversationThunk, getConversation, } from '../redux/thunks/conversationThunks';
import { EventProcessor } from '../services/EventProcessor';
import { SSEService } from '../services/SSEService';
const AssistantContext = createContext({
    sseService: null,
    eventProcessor: null,
    sendMessage: async () => {
        throw new Error('AssistantProvider not initialized');
    },
    createConversation: async () => {
        throw new Error('AssistantProvider not initialized');
    },
    user: {
        id: '',
        name: '',
        type: '',
    },
    usageContext: undefined,
    grantProfileNamesById: undefined,
    isAutoScrollEnabled: true,
    setIsAutoScrollEnabled: () => { },
    agentMode: null,
    setAgentMode: () => { },
});
AssistantContext.displayName = 'AssistantContext';
const AssistantProvider = ({ children, conversationId, user, usageContext, entityNameDisplay, grantProfileNamesById, manageRouting = true, }) => {
    var _a, _b, _c, _d;
    const { accessToken } = useLogin();
    const dispatch = useAssistantDispatch();
    const sseServiceRef = useRef(null);
    const sseConversationIdRef = useRef(null);
    const eventProcessorRef = useRef(null);
    const lastModelRef = useRef(undefined);
    const sseUnsubscribeRef = useRef(null);
    const activeConversation = useAssistantSelector((state) => selectActiveConversation(state));
    const api = useAssistantApi();
    const baseApiUrl = useApiUrl();
    const { navigate, pathname, search } = useLocation();
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(false);
    const agentMode = (_a = activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.agent_mode) !== null && _a !== void 0 ? _a : null;
    const currentConversationId = (_c = (_b = activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id) !== null && _b !== void 0 ? _b : conversationId) !== null && _c !== void 0 ? _c : null;
    const currentConversationStillProcessing = useAssistantSelector((state) => currentConversationId
        ? selectConversationStillProcessing(state, currentConversationId)
        : false);
    const hasDeferredProcessingNotice = useAssistantSelector((state) => currentConversationId
        ? !!state.assistant.processingNoticeConversationIds[currentConversationId]
        : false);
    const previousConversationRef = useRef({
        id: null,
        wasStillProcessing: false,
    });
    const activeConversationIdRef = useRef((_d = activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id) !== null && _d !== void 0 ? _d : null);
    const isResolvingRequestedConversationRef = useRef(false);
    const didInitializeEmbeddedConversationRef = useRef(false);
    const pendingRequestedConversationIdRef = useRef(null);
    const syncEmbeddedConversationParam = useCallback((nextConversationId) => {
        const nextParams = new URLSearchParams(search);
        if (nextConversationId) {
            nextParams.set(ASSISTANT_EMBEDDED_CONVERSATION_PARAM, nextConversationId);
        }
        else {
            nextParams.delete(ASSISTANT_EMBEDDED_CONVERSATION_PARAM);
        }
        const nextSearch = nextParams.toString();
        const nextUrl = nextSearch ? `${pathname}?${nextSearch}` : pathname;
        navigate(nextUrl, { replace: true, preventScrollReset: true });
    }, [navigate, pathname, search]);
    const initSSE = (convId) => {
        var _a;
        if (sseConversationIdRef.current === convId &&
            sseServiceRef.current &&
            eventProcessorRef.current) {
            return;
        }
        (_a = eventProcessorRef.current) === null || _a === void 0 ? void 0 : _a.reset();
        if (sseUnsubscribeRef.current) {
            try {
                sseUnsubscribeRef.current();
            }
            catch (_) {
                console.error('Failed to unsubscribe from SSE');
            }
            sseUnsubscribeRef.current = null;
        }
        if (sseServiceRef.current) {
            try {
                sseServiceRef.current.disconnect();
            }
            catch (_) {
                console.error('Failed to disconnect SSE');
            }
            sseServiceRef.current = null;
        }
        sseConversationIdRef.current = null;
        eventProcessorRef.current = new EventProcessor(dispatch, convId);
        const sseService = new SSEService({
            baseApiUrl,
            authToken: accessToken,
            onError: (error) => {
                console.error('[AssistantProvider] SSE error:', error);
            },
        });
        sseServiceRef.current = sseService;
        sseConversationIdRef.current = convId;
        eventProcessorRef.current.setOnContinuation(() => {
            void sseService
                .sendMessage(convId, null, lastModelRef.current, undefined)
                .catch((err) => {
                console.error('[AssistantProvider] Continuation failed:', err);
            });
        });
        const unsubscribe = sseService.on('stream', (event) => {
            var _a;
            (_a = eventProcessorRef.current) === null || _a === void 0 ? void 0 : _a.processSSEEvent(event);
        });
        sseUnsubscribeRef.current = unsubscribe;
    };
    useEffect(() => {
        const previousConversation = previousConversationRef.current;
        if (previousConversation.id &&
            previousConversation.id !== currentConversationId &&
            previousConversation.wasStillProcessing) {
            dispatch(markConversationProcessingNotice(previousConversation.id));
            dispatch(setAssistantStatus(AssistantStatus.IDLE));
        }
        previousConversationRef.current = {
            id: currentConversationId,
            wasStillProcessing: currentConversationStillProcessing,
        };
    }, [currentConversationId, currentConversationStillProcessing, dispatch]);
    useEffect(() => {
        return () => {
            const previousConversation = previousConversationRef.current;
            if (previousConversation.id && previousConversation.wasStillProcessing) {
                dispatch(markConversationProcessingNotice(previousConversation.id));
            }
            dispatch(setAssistantStatus(AssistantStatus.IDLE));
        };
    }, [dispatch]);
    useEffect(() => {
        if (currentConversationId &&
            hasDeferredProcessingNotice &&
            !currentConversationStillProcessing) {
            dispatch(clearConversationProcessingNotice(currentConversationId));
        }
    }, [
        currentConversationId,
        currentConversationStillProcessing,
        dispatch,
        hasDeferredProcessingNotice,
    ]);
    const statusRef = useRef(AssistantStatus.IDLE);
    const currentStatus = useAssistantSelector(selectAssistantStatus);
    statusRef.current = currentStatus;
    useEffect(() => {
        const STALE_THRESHOLD_MS = 30000;
        const GRACE_PERIOD_MS = 2000;
        const busyStatuses = [
            AssistantStatus.THINKING,
            AssistantStatus.TOOL_EXECUTING,
            AssistantStatus.RESPONDING,
        ];
        let graceTimeoutId = null;
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible')
                return;
            if (graceTimeoutId !== null)
                clearTimeout(graceTimeoutId);
            graceTimeoutId = setTimeout(() => {
                var _a, _b;
                graceTimeoutId = null;
                if (!busyStatuses.includes(statusRef.current))
                    return;
                const lastEvent = (_b = (_a = eventProcessorRef.current) === null || _a === void 0 ? void 0 : _a.getLastEventTimestamp()) !== null && _b !== void 0 ? _b : 0;
                const staleDuration = Date.now() - lastEvent;
                if (staleDuration > STALE_THRESHOLD_MS) {
                    console.warn(`[AssistantProvider] Resetting stale busy status (${statusRef.current}) — no SSE event for ${Math.round(staleDuration / 1000)}s`);
                    dispatch(setAssistantStatus(AssistantStatus.IDLE));
                }
            }, GRACE_PERIOD_MS);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (graceTimeoutId !== null)
                clearTimeout(graceTimeoutId);
        };
    }, [dispatch]);
    useEffect(() => {
        var _a;
        activeConversationIdRef.current = (_a = activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id) !== null && _a !== void 0 ? _a : null;
    }, [activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id]);
    useEffect(() => {
        if (!(activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id) || !entityNameDisplay) {
            return;
        }
        const changes = {
            id: activeConversation.id,
        };
        let hasChanges = false;
        if ((usageContext === null || usageContext === void 0 ? void 0 : usageContext.company_id) &&
            activeConversation.company_name !== entityNameDisplay) {
            changes.company_name = entityNameDisplay;
            hasChanges = true;
        }
        if ((usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id) &&
            activeConversation.application_display !== entityNameDisplay) {
            changes.application_display = entityNameDisplay;
            hasChanges = true;
        }
        if (hasChanges) {
            dispatch(updateConversation(changes));
        }
    }, [
        activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.application_display,
        activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.company_name,
        activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id,
        dispatch,
        entityNameDisplay,
        usageContext === null || usageContext === void 0 ? void 0 : usageContext.company_id,
        usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id,
    ]);
    useEffect(() => {
        if (manageRouting || didInitializeEmbeddedConversationRef.current) {
            return;
        }
        didInitializeEmbeddedConversationRef.current = true;
        if (!conversationId && !!(activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id)) {
            dispatch(setActiveConversation(null));
        }
    }, [manageRouting, conversationId, activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id, dispatch]);
    useEffect(() => {
        if (!conversationId) {
            pendingRequestedConversationIdRef.current = null;
            isResolvingRequestedConversationRef.current = false;
            return;
        }
        if (conversationId === activeConversationIdRef.current) {
            pendingRequestedConversationIdRef.current = null;
            isResolvingRequestedConversationRef.current = false;
            return;
        }
        const requestedConversationId = conversationId;
        pendingRequestedConversationIdRef.current = requestedConversationId;
        if (!manageRouting) {
            isResolvingRequestedConversationRef.current = true;
        }
        dispatch(getConversation({ api, conversationId: requestedConversationId }))
            .unwrap()
            .then((conversation) => {
            if (pendingRequestedConversationIdRef.current !== requestedConversationId) {
                return;
            }
            const conversationWithDisplayOverride = Object.assign(Object.assign({}, conversation), { company_name: (usageContext === null || usageContext === void 0 ? void 0 : usageContext.company_id) && entityNameDisplay
                    ? entityNameDisplay
                    : conversation.company_name, application_display: (usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id) && entityNameDisplay
                    ? entityNameDisplay
                    : conversation.application_display });
            pendingRequestedConversationIdRef.current = null;
            isResolvingRequestedConversationRef.current = false;
            dispatch(addConversation(conversationWithDisplayOverride));
            dispatch(setActiveConversation(conversationWithDisplayOverride.id));
            if (manageRouting) {
                if (conversationWithDisplayOverride.id &&
                    window.location.pathname !==
                        `/assistant/${conversationWithDisplayOverride.id}`) {
                    navigate(`/assistant/${conversationWithDisplayOverride.id}`, {
                        replace: true,
                        preventScrollReset: true,
                    });
                }
            }
        })
            .catch((error) => {
            if (pendingRequestedConversationIdRef.current !== requestedConversationId) {
                return;
            }
            pendingRequestedConversationIdRef.current = null;
            isResolvingRequestedConversationRef.current = false;
            console.error('[AssistantProvider] Failed to load conversation:', error);
            if (!manageRouting) {
                dispatch(clearConversationBlockingError());
                syncEmbeddedConversationParam(null);
            }
            else {
                navigate('/assistant', { replace: true });
            }
        });
    }, [
        api,
        conversationId,
        dispatch,
        navigate,
        manageRouting,
        syncEmbeddedConversationParam,
    ]);
    useEffect(() => {
        var _a;
        if (manageRouting) {
            if (conversationId &&
                pendingRequestedConversationIdRef.current === conversationId &&
                conversationId !== (activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id)) {
                return;
            }
            if (!(activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id))
                return;
            const wanted = `/assistant/${activeConversation.id}`;
            if (window.location.pathname !== wanted) {
                navigate(wanted, { replace: true, preventScrollReset: true });
            }
            return;
        }
        if (isResolvingRequestedConversationRef.current &&
            !!conversationId &&
            conversationId !== (activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id)) {
            return;
        }
        const currentEmbeddedConversationId = new URLSearchParams(search).get(ASSISTANT_EMBEDDED_CONVERSATION_PARAM);
        const nextEmbeddedConversationId = (_a = activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id) !== null && _a !== void 0 ? _a : null;
        if (currentEmbeddedConversationId !== nextEmbeddedConversationId) {
            syncEmbeddedConversationParam(nextEmbeddedConversationId);
        }
    }, [
        activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id,
        conversationId,
        navigate,
        manageRouting,
        search,
        syncEmbeddedConversationParam,
    ]);
    useEffect(() => {
        const convId = currentConversationId;
        if (!convId)
            return;
        initSSE(convId);
        return () => {
            var _a, _b;
            if (sseConversationIdRef.current !== convId) {
                return;
            }
            try {
                (_a = sseUnsubscribeRef.current) === null || _a === void 0 ? void 0 : _a.call(sseUnsubscribeRef);
            }
            catch (_) {
                console.error('Failed to unsubscribe from SSE');
            }
            sseUnsubscribeRef.current = null;
            try {
                (_b = sseServiceRef.current) === null || _b === void 0 ? void 0 : _b.disconnect();
            }
            catch (_) {
                console.error('Failed to disconnect SSE');
            }
            sseServiceRef.current = null;
            eventProcessorRef.current = null;
            sseConversationIdRef.current = null;
        };
    }, [currentConversationId, accessToken, baseApiUrl]);
    const sendMessage = async (content, activeConversationId, model, is_onboarding) => {
        var _a;
        const convId = (_a = activeConversationId !== null && activeConversationId !== void 0 ? activeConversationId : activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id) !== null && _a !== void 0 ? _a : conversationId;
        if (!convId) {
            throw new Error('No conversation ID available');
        }
        if (!sseServiceRef.current || sseConversationIdRef.current !== convId) {
            initSSE(convId);
        }
        const sse = sseServiceRef.current;
        if (!sse) {
            throw new Error('SSE service not initialized');
        }
        lastModelRef.current = model;
        const id = await sse.sendMessage(convId, content, model, is_onboarding);
        return id;
    };
    const createConversation = async (requestedUsageContext) => {
        var _a, _b;
        dispatch(clearConversationBlockingError());
        dispatch(setAssistantStatus(AssistantStatus.IDLE));
        const effectiveUsageContext = usageContext !== null && usageContext !== void 0 ? usageContext : requestedUsageContext;
        const createdConversation = await dispatch(createConversationThunk({
            api,
            usageContext: effectiveUsageContext,
        })).unwrap();
        let conversation = createdConversation;
        const shouldHydrateCreatedConversation = (!!((_a = createdConversation.usage_context) === null || _a === void 0 ? void 0 : _a.company_id) &&
            !createdConversation.company_name) ||
            (!!((_b = createdConversation.usage_context) === null || _b === void 0 ? void 0 : _b.grant_application_id) &&
                !createdConversation.application_display);
        if (shouldHydrateCreatedConversation) {
            try {
                conversation = await dispatch(getConversation({
                    api,
                    conversationId: createdConversation.id,
                })).unwrap();
            }
            catch (error) {
                console.error('[AssistantProvider] Failed to hydrate created conversation context:', error);
            }
        }
        conversation = Object.assign(Object.assign({}, conversation), { company_name: (effectiveUsageContext === null || effectiveUsageContext === void 0 ? void 0 : effectiveUsageContext.company_id) && entityNameDisplay
                ? entityNameDisplay
                : conversation.company_name, application_display: (effectiveUsageContext === null || effectiveUsageContext === void 0 ? void 0 : effectiveUsageContext.grant_application_id) && entityNameDisplay
                ? entityNameDisplay
                : conversation.application_display });
        dispatch(addConversation(conversation));
        dispatch(setActiveConversation(conversation.id));
        dispatch(updateConversationActivity(conversation.id));
        return conversation;
    };
    const handleAgentModeChange = useCallback(async (mode) => {
        const convId = activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id;
        if (!convId)
            return;
        if (!(usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id))
            return;
        dispatch(updateConversation({ id: convId, agent_mode: mode }));
        try {
            const syntheticMessages = await api.updateConversationAgentMode(convId, mode);
            for (const msg of syntheticMessages) {
                dispatch(addMessage(msg));
            }
        }
        catch (err) {
            console.error('[AssistantProvider] Failed to persist agent_mode:', err);
        }
    }, [activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.id, api, dispatch, usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id]);
    const value = {
        sseService: sseServiceRef.current,
        eventProcessor: eventProcessorRef.current,
        sendMessage,
        createConversation,
        user,
        usageContext,
        grantProfileNamesById,
        isAutoScrollEnabled,
        setIsAutoScrollEnabled,
        agentMode,
        setAgentMode: handleAgentModeChange,
    };
    return (_jsx(AssistantContext.Provider, { value: value, children: children }));
};
export const useAssistantContext = () => {
    const context = useContext(AssistantContext);
    if (!context) {
        throw new Error('useAssistantContext must be used within AssistantProvider');
    }
    return context;
};
export default AssistantProvider;
